import React, { useState, useEffect, useRef } from 'react';
import { useSalon } from '../contexts/SalonContext';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import './BusinessSettings.css';

// Material UI (lightweight, tree‑shakable) components
import { Button, LinearProgress, Grid, Card, CardContent, IconButton } from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const BusinessSettings = () => {
    const { currentBusiness, updateBusiness } = useSalon();
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [hours, setHours] = useState<any>({});
    const [footerText, setFooterText] = useState('');
    const [googleMapsUrl, setGoogleMapsUrl] = useState('');
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Logo upload state
    const logoInputRef = useRef<HTMLInputElement>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [logoMsg, setLogoMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Banner upload state
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerMsg, setBannerMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Illustration upload state (max 10)
  const illustrationInputRef = useRef<HTMLInputElement>(null);
  const [selectedIllustrations, setSelectedIllustrations] = useState<File[]>([]);
  const [illustrationPreviews, setIllustrationPreviews] = useState<string[]>([]);
  const [uploadingIllustrations, setUploadingIllustrations] = useState(false);
  const [illustrationMsg, setIllustrationMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    useEffect(() => {
        if (currentBusiness?.settings?.businessHours) {
            setHours(currentBusiness.settings.businessHours);
        } else {
            const initialHours: any = {};
            DAYS.forEach(day => {
                initialHours[day] = { open: '09:00', close: '17:00', closed: false };
            });
            setHours(initialHours);
        }

        if (currentBusiness?.settings?.bookingPage?.footerText) {
            setFooterText(currentBusiness.settings.bookingPage.footerText);
        } else {
            setFooterText('');
        }

        // Load Google Maps URL
        const url = (currentBusiness as any)?.googleMapsUrl || '';
        console.log('[BusinessSettings] Loading googleMapsUrl from currentBusiness:', url, '| raw:', (currentBusiness as any)?.googleMapsUrl);
        setGoogleMapsUrl(url);
    }, [currentBusiness]);

    const handleChange = (day: string, field: string, value: any) => {
        setHours((prev: any) => ({
            ...prev,
            [day]: { ...prev[day], [field]: value }
        }));
    };

    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setLogoMsg({ type: 'error', text: '❌ Image too large. Max 5MB.' });
      return;
    }
    setLogoPreview(URL.createObjectURL(file));
    try {
      setUploadingLogo(true);
      setLogoMsg({ type: 'success', text: `⏳ ${t('business.uploadingLogo')}` });
      const { url } = await apiService.uploadBusinessLogo(file);
      const updatedSettings = { ...currentBusiness?.settings, logo: url };
      await updateBusiness(currentBusiness!.id, { settings: updatedSettings });
      setLogoMsg({ type: 'success', text: `✅ ${t('business.logo')} updated!` });
      setLogoPreview(null);
    } catch (err: any) {
      setLogoMsg({ type: 'error', text: '❌ ' + (err.message || 'Upload failed') });
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleBannerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setBannerMsg({ type: 'error', text: '❌ Image too large. Max 5MB.' });
      return;
    }
    setBannerPreview(URL.createObjectURL(file));
    try {
      setUploadingBanner(true);
      setBannerMsg({ type: 'success', text: `⏳ ${t('business.uploadingBanner')}` });
      const { url } = await apiService.uploadBanner(file);
      const updatedSettings = { ...currentBusiness?.settings, banner: url };
      await updateBusiness(currentBusiness!.id, { settings: updatedSettings });
      setBannerMsg({ type: 'success', text: `✅ ${t('business.banner')} updated!` });
      setBannerPreview(null);
    } catch (err: any) {
      setBannerMsg({ type: 'error', text: '❌ ' + (err.message || 'Upload failed') });
    } finally {
      setUploadingBanner(false);
      e.target.value = '';
    }
  };

  const handleIllustrationsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (!files.length) return;
    const existingCount = currentBusiness?.illustrations?.length || 0;
    if (existingCount + selectedIllustrations.length + files.length > 10) {
      setIllustrationMsg({ type: 'error', text: '❌ Maximum of 10 illustrations allowed.' });
      return;
    }
    const oversized = files.find(f => f.size > 5 * 1024 * 1024);
    if (oversized) {
      setIllustrationMsg({ type: 'error', text: '❌ One or more images exceed 5MB.' });
      return;
    }
    const newPreviews = files.map(f => URL.createObjectURL(f));
    setSelectedIllustrations(prev => [...prev, ...files]);
    setIllustrationPreviews(prev => [...prev, ...newPreviews]);
  };

  const uploadIllustrationsHandler = async () => {
    if (!selectedIllustrations.length) return;
    try {
      setUploadingIllustrations(true);
      setIllustrationMsg({ type: 'success', text: `⏳ ${t('business.uploadingIllustrations')}` });
      const { urls } = await apiService.uploadIllustrations(selectedIllustrations);
      const updatedIllustrations = [...(currentBusiness?.illustrations || []), ...urls];
      await updateBusiness(currentBusiness!.id, { illustrations: updatedIllustrations });
      setIllustrationMsg({ type: 'success', text: `✅ Added ${urls.length} images!` });
      setSelectedIllustrations([]);
      setIllustrationPreviews([]);
    } catch (err: any) {
      setIllustrationMsg({ type: 'error', text: '❌ ' + (err.message || 'Upload failed') });
    } finally {
      setUploadingIllustrations(false);
    }
  };

    const handleSave = async () => {
        setLoading(true);
        setMessage(null);
        if (!currentBusiness?.id) return;

        try {
            const updatedSettings = {
                ...currentBusiness.settings,
                businessHours: hours,
                bookingPage: {
                    ...(currentBusiness.settings?.bookingPage || {}),
                    footerText: footerText.trim() || undefined
                }
            };

            console.log('[BusinessSettings] Sending update with googleMapsUrl:', googleMapsUrl.trim() || null);
            const result = await updateBusiness(currentBusiness.id, {
                settings: updatedSettings,
                googleMapsUrl: googleMapsUrl.trim() || null
            } as any);
            console.log('[BusinessSettings] Update complete. currentBusiness.googleMapsUrl is now:', (currentBusiness as any)?.googleMapsUrl);
            setMessage({ type: 'success', text: t('business.settingsUpdated') });
        } catch (error) {
            console.error(error);
            setMessage({ type: 'error', text: t('business.settingsError') });
        } finally {
            setLoading(false);
        }
    };

    if (!currentBusiness) return <div>{t('business.loading')}</div>;

    const currentLogo = logoPreview || currentBusiness?.settings?.logo;

    return (
        <div className="business-settings-container">
            <h2>{t('business.settings')}</h2>
            {message && <div className={`message ${message.type}`}>{message.text}</div>}

            {/* ── BUSINESS LOGO ── */}
            <div className="settings-section">
                <h3>🏢 {t('business.logo')}</h3>
                <div className="logo-upload-area">
                    <div
                        className={`logo-dropzone ${uploadingLogo ? 'uploading' : ''}`}
                        onClick={() => !uploadingLogo && logoInputRef.current?.click()}
                        title={uploadingLogo ? t('business.uploadingLogo') : t('business.uploadLogo')}
                    >
                        {currentLogo ? (
                            <img src={currentLogo} alt="Business logo" className="logo-preview-img" />
                        ) : (
                            <div className="logo-placeholder">
                                <span style={{ fontSize: '2.5rem' }}>🏢</span>
                                <p>{t('business.uploadLogo')}</p>
                                <p style={{ fontSize: '0.75rem', opacity: 0.6 }}>{t('business.uploadLogoHint')}</p>
                            </div>
                        )}
                        {uploadingLogo && (
                            <div className="logo-uploading-overlay">
                                <div className="settings-spinner" />
                            </div>
                        )}
                    </div>
                    <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/svg+xml"
                        style={{ display: 'none' }}
                        onChange={handleLogoChange}
                    />
                    {logoMsg && (
                        <p className={`logo-msg ${logoMsg.type}`}>{logoMsg.text}</p>
                    )}
                    {currentLogo && !uploadingLogo && (
                        <button
                            className="logo-change-btn"
                            onClick={() => logoInputRef.current?.click()}
                        >
                            {t('business.changeLogo')}
                        </button>
                    )}
                </div>
            </div>

            {/* ── ONLINE BOOKING PAGE SETTINGS ── */}
            <div className="settings-section">
                <h3>🌐 {t('business.onlineBookingPage')}</h3>
                <div className="settings-group">
                    <label htmlFor="footerText">
                        {t('business.footerMessage')}
                        <span className="label-hint"> {t('business.footerHint')}</span>
                    </label>
                    <input
                        id="footerText"
                        type="text"
                        className="settings-input"
                        value={footerText}
                        onChange={(e) => setFooterText(e.target.value)}
                        placeholder={
                            currentBusiness.phone
                                ? `Need assistance? Call us: ${currentBusiness.phone}`
                                : 'Need assistance? Please contact us directly.'
                        }
                        maxLength={120}
                    />
                    <span className="field-hint">{t('business.footerBlankHint')}</span>
      {/* Illustration gallery upload */}
      <div className="illustration-section" style={{ marginTop: '20px' }}>
        <h4>{t('business.illustrations')}</h4>
        <input
          ref={illustrationInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/svg+xml"
          multiple
          style={{ display: 'none' }}
          onChange={handleIllustrationsChange}
        />
        <Button variant="contained" startIcon={<PhotoCamera />} onClick={() => illustrationInputRef.current?.click()} disabled={uploadingIllustrations}>
          {t('business.addIllustrations')}
        </Button>
        {illustrationPreviews.length > 0 && (
          <Grid container spacing={2} style={{ marginTop: '10px' }}>
            {illustrationPreviews.map((src, idx) => (
              <Grid item xs={3} key={idx}>
                <Card>
                  <CardContent>
                    <img src={src} alt={`Illustration ${idx + 1}`} style={{ width: '100%', height: 'auto' }} />
                    <IconButton size="small" onClick={() => {
                      const newFiles = [...selectedIllustrations];
                      const newPreviews = [...illustrationPreviews];
                      newFiles.splice(idx, 1);
                      newPreviews.splice(idx, 1);
                      setSelectedIllustrations(newFiles);
                      setIllustrationPreviews(newPreviews);
                    }}><DeleteIcon fontSize="small" /></IconButton>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        {selectedIllustrations.length > 0 && (
          <Button variant="outlined" color="primary" onClick={uploadIllustrationsHandler} disabled={uploadingIllustrations} startIcon={<CloudUploadIcon />}> {t('business.uploadSelected')} </Button>
        )}
        {illustrationMsg && <p className={`illustration-msg ${illustrationMsg.type}`}>{illustrationMsg.text}</p>}
      </div>
                </div>
            </div>

            {/* ── BUSINESS HOURS ── */}
            <div className="settings-section">
                <h3>🕐 {t('business.businessHours')}</h3>
                <div className="hours-grid">
                    {DAYS.map(day => (
                        <div key={day} className={`day-row ${hours[day]?.closed ? 'closed' : ''}`}>
                            <div className="day-name">{day}</div>
                            <div className="day-controls">
                                <label className="toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={!hours[day]?.closed}
                                        onChange={(e) => handleChange(day, 'closed', !e.target.checked)}
                                    />
                                    {t('business.open')}
                                </label>

                                {!hours[day]?.closed && (
                                    <>
                                        <input
                                            type="time"
                                            value={hours[day]?.open || '09:00'}
                                            onChange={(e) => handleChange(day, 'open', e.target.value)}
                                        />
                                        <span>{t('business.to')}</span>
                                        <input
                                            type="time"
                                            value={hours[day]?.close || '17:00'}
                                            onChange={(e) => handleChange(day, 'close', e.target.value)}
                                        />
                                    </>
                                )}
                                {hours[day]?.closed && <span className="closed-text">{t('business.closed')}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── GOOGLE MAPS & QR CODE ── */}
            <div className="settings-section">
                <h3>🗺️ Google Maps & QR Check-in</h3>
                <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '12px' }}>
                    {t('business.googleMapsHint') || 'Nhập link Google Maps để khách 5 sao có thể đánh giá trực tiếp. Mã QR của business được tạo tự động từ Booking Code.'}
                </p>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ fontWeight: 600, marginBottom: '6px', display: 'block' }}>
                        🔗 Google Maps Review URL
                    </label>
                    <input
                        type="text"
                        value={googleMapsUrl}
                        onChange={e => setGoogleMapsUrl(e.target.value)}
                        placeholder="https://g.page/r/your-place/review"
                        style={{
                            width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0',
                            borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'
                        }}
                    />
                </div>
                {currentBusiness?.bookingCode && (
                    <div style={{ background: '#f0fff4', border: '1px solid #c6f6d5', borderRadius: '10px', padding: '12px 16px' }}>
                        <div style={{ fontSize: '13px', color: '#276749', fontWeight: 600 }}>
                            📱 QR Check-in Link:
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: '13px', color: '#2d3748', marginTop: '4px', wordBreak: 'break-all' }}>
                            {window.location.origin}/checkin/{currentBusiness.bookingCode}
                        </div>
                        <div style={{ fontSize: '12px', color: '#718096', marginTop: '6px' }}>
                            {t('business.qrPrintHint') || 'ℹ️ In mã QR từ Dashboard → nút "📱 QR Check-in" để dán tại quầy lễ tân'}
                        </div>
                    </div>
                )}
            </div>

            <button className="save-btn" onClick={handleSave} disabled={loading}>
                {loading ? t('business.saving') : t('business.save')}
            </button>
        </div>
    );
};

export default BusinessSettings;
